/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, RangeSlider } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field } from 'redux-form';

import ShopifyFonts from 'src/constants/shopify-fonts.json';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderSelect3V2 } from 'src/components/views/common/forms/render-select-3-v2';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import {
  roundedFontSizeScale,
  updateElementTypoRespective
} from 'src/helpers/shopify-integration-settings-helper';

import {
  ELEMENTS,
  MENU_ITEM_VALUE,
  FONT_FAMILY_INHERIT,
  TEXT_TRANSFORM_OPTIONS
} from 'src/constants/theme-settings.js';
import { DropdownIndicator, GroupHeading, styles } from './react-select-style';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void,
  updateFieldReducer: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const MAP_TYPOGRAPHY_SECTION_FORM = {
  [MENU_ITEM_VALUE.TYPOGRAPHY_HEADING]: ELEMENTS.TYPOGRAPHY_HEADING,
  [MENU_ITEM_VALUE.TYPOGRAPHY_BODY]: ELEMENTS.TYPOGRAPHY_BODY,
  [MENU_ITEM_VALUE.TYPOGRAPHY_BUTTON]: ELEMENTS.TYPOGRAPHY_BUTTON,
  [MENU_ITEM_VALUE.TYPOGRAPHY_LINK_ACCENT]: ELEMENTS.TYPOGRAPHY_LINK_ACCENT
};

class TypographySectionForm extends Component<ComposedProps> {
  state = {};

  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField, activeMenuItem } = nextProps;

    const typographyKey = MAP_TYPOGRAPHY_SECTION_FORM[activeMenuItem];

    const fontFamily = _.get(formValues, `${typographyKey}.fontFamily`, '');
    const nextFontFamily = _.get(nextFormValues, `${typographyKey}.fontFamily`, '');
    const typography = _.cloneDeep(_.get(formValues, typographyKey));
    const typographyPayload = _.cloneDeep(_.get(nextFormValues, typographyKey));
    if (fontFamily !== nextFontFamily) {
      let payload = '';
      if (nextFontFamily === FONT_FAMILY_INHERIT) {
        payload = FONT_FAMILY_INHERIT;
      } else {
        const font = ShopifyFonts.find(font => font?.variants[0]?.family === nextFontFamily);
        const value = `${font?.variants[0]?.style}-${font?.variants[0]?.weight}`;
        payload = value;
      }
      _.set(typographyPayload, 'fontStyle', payload);
      updateField(`${typographyKey}.fontStyle`, payload);
    }

    // Call func to update related element
    if (!_.isEqual(typography, typographyPayload)) {
      const settingDB = _.cloneDeep(_.get(nextFormValues, 'setting'));
      const pagination = _.cloneDeep(_.get(nextFormValues, 'pagination'));
      const { setting, isChangedPagination, paginationSetting } = updateElementTypoRespective(
        typographyPayload,
        settingDB,
        pagination
      );

      updateField(`setting`, setting);
      if (isChangedPagination) {
        updateField('pagination', paginationSetting);
      }
    }
  }

  // Handle blur input
  onBlurTextField = (event, value, name) => {
    event.preventDefault();
    const { formValues, activeMenuItem, updateField } = this.props;
    const typographyKey = MAP_TYPOGRAPHY_SECTION_FORM[activeMenuItem];
    const fontSizeScaleRange = _.get(formValues, `${typographyKey}.fontSizeScaleRange`);

    if (Number(value) < fontSizeScaleRange[0]) {
      updateField(name, fontSizeScaleRange[0]);
    } else if (Number(value) > fontSizeScaleRange[1]) {
      updateField(name, fontSizeScaleRange[1]);
    } else {
      const roundedNumber = roundedFontSizeScale(Number(value));
      updateField(name, roundedNumber);
    }
  };

  render() {
    const { i18n, activeMenuItem, formValues, updateField } = this.props;

    const typographyKey = MAP_TYPOGRAPHY_SECTION_FORM[activeMenuItem];
    const formattedShopifyFonts = ShopifyFonts.map(item => {
      return {
        ...item,
        label: item.name,
        value: item?.variants[0]?.family || item.default_variant_handle
      };
    });
    const formattedFonts = [
      {
        label: '',
        options: [
          {
            label: i18n.translate(
              'AdminHTML.ShopifyIntegrationSettings.Form.typography_InheritFont'
            ),
            value: FONT_FAMILY_INHERIT
          }
        ]
      },
      {
        label: '',
        options: formattedShopifyFonts
      }
    ];

    const fontFamily = _.get(formValues, `${typographyKey}.fontFamily`);
    const fontSizeScale = _.get(formValues, `${typographyKey}.fontSizeScale`);
    const fontSizeScaleRange = _.get(formValues, `${typographyKey}.fontSizeScaleRange`);

    let formattedVariant = [
      {
        label: i18n.translate('AdminHTML.ShopifyIntegrationSettings.Form.typography_InheritFont'),
        value: FONT_FAMILY_INHERIT
      }
    ];

    if (fontFamily !== FONT_FAMILY_INHERIT) {
      const font = ShopifyFonts.find(font => font?.variants[0]?.family === fontFamily);
      formattedVariant = (font?.variants || []).map(item => {
        const value = `${item?.style}-${item?.weight}`;
        return {
          ...item,
          label: item.name,
          value: value
        };
      });
    }

    return (
      <div className="typography-form">
        <FormLayout>
          <div className="form-section" style={{ marginTop: '22px' }}>
            {/* Font family */}
            <div className="form-section__item form-section__item--standard-spacing">
              <Field
                isGroup
                name={`${typographyKey}.fontFamily`}
                component={RenderSelect2}
                options={formattedFonts}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.fontFamily_Title')}
                components={{ DropdownIndicator, GroupHeading }}
                styles={{
                  ...styles,
                  menuList: baseStyles => ({
                    ...baseStyles,
                    padding: '8px',
                    maxHeight: '250px'
                  })
                }}
                placeholder=""
                listOptionForSearching={formattedFonts[1]?.options || []}
              />
            </div>

            {/* Font style */}
            <div className="form-section__item form-section__item--medium-spacing">
              <Field
                name={`${typographyKey}.fontStyle`}
                component={RenderSelect3V2}
                options={formattedVariant}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.fontStyle_Title')}
                components={{ DropdownIndicator }}
                styles={styles}
                isSearchable={false}
                placeholderEmpty=""
              />
            </div>

            {/* Font size scale */}
            {fontFamily !== FONT_FAMILY_INHERIT && (
              <div className="form-section__item form-section__item--slider form-section__item--medium-spacing">
                <Field
                  name={`${typographyKey}.fontSizeScale`}
                  component={RenderTextField}
                  type="number"
                  min={fontSizeScaleRange[0]}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.typography_FontSizeScale'
                  )}
                  // normalize={(value, prevValue) =>
                  //   normalizeNumberRangeSlider(value, prevValue, fontSizeScaleRange[1])
                  // }
                  onBlur={(event, value) =>
                    this.onBlurTextField(event, value, `${typographyKey}.fontSizeScale`)
                  }
                  anotherProps={{
                    connectedLeft: (
                      <RangeSlider
                        value={fontSizeScale}
                        step={5}
                        onChange={value => updateField(`${typographyKey}.fontSizeScale`, value)}
                        min={fontSizeScaleRange[0]}
                        max={fontSizeScaleRange[1]}
                      />
                    ),
                    connectedRight: (
                      <div className="text-field-unit">
                        <span>%</span>
                      </div>
                    )
                  }}
                />
                {Number(fontSizeScale) < fontSizeScaleRange[0] && (
                  <div className="Polaris-Labelled__Error">
                    <div className="Polaris-InlineError">
                      {i18n.translate('AdminHTML.Helpers.FormValidationHelper.minValue', {
                        nt_number: fontSizeScaleRange[0]
                      })}
                    </div>
                  </div>
                )}
                {Number(fontSizeScale) > fontSizeScaleRange[1] && (
                  <div className="Polaris-Labelled__Error">
                    <div className="Polaris-InlineError">
                      {i18n.translate('AdminHTML.Helpers.FormValidationHelper.maxValue2', {
                        nt_number: fontSizeScaleRange[1]
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="form-section__item form-section__item--medium-spacing">
              <p className="subtitle">
                {i18n.translate('AdminHTML.ThemeSetting.Form.textTransform_Title')}
              </p>
              <div className="radio-button-wrapper">
                {TEXT_TRANSFORM_OPTIONS.map((item, index) => (
                  <Field
                    key={index}
                    name={`${typographyKey}.textTransform`}
                    id={item.value}
                    component={RenderRadioButton2}
                    label={item.label}
                  />
                ))}
              </div>
            </div>
          </div>
        </FormLayout>
      </div>
    );
  }
}

export default withI18n()(TypographySectionForm);
